import Vue from 'vue'
import VueResource from 'vue-resource'
import moment from 'moment'
import * as _ from 'lodash'
import Utils from '@/providers/Utils'

Vue.use(VueResource)

/**
 * Interface to the Yii api
 */
export default {

	// main entry point for Yii2 api
	apiUrl: process.env.VUE_APP_API_HOST ? `${process.env.VUE_APP_API_HOST}/` : 'http://cacc-api.test/',

  loadEnvironment () {
    return Vue.http.post(`${this.apiUrl}?r=site/environment`)
  },

  loadSystem () {
    return Vue.http.post(`${this.apiUrl}?r=site/system`)
  },

  // --------- user functions ------------

	// (these mirror the corresponding functions in User.js, but we put them here to encapsulate
	// all actual http actions and references to apiUrl)

	async emailExists (email) {
		email = encodeURIComponent(email)
		return Vue.http.get(`${this.apiUrl}?r=user/email-exists&email=${email}`)
	},

	checkAuth () {
		// if stored yii2User.auth_key is invalid, this will trigger a redirect to the login page
		// (see Utils.addAuthorizationInterceptor)
		return Vue.http.get(`${this.apiUrl}?r=user/check-auth`)
	},

	loginUser (vue, email, password) {
		return Vue.http.post(
			`${this.apiUrl}?r=user/login`,
			{ email: email, password: password }
		)
	},

	loginTwofa (vue, token, code) {
		return Vue.http.post(
			`${this.apiUrl}?r=user/twofa`,
			{ token: token, code: code }
		)
	},

	logoutUser (vue, userId) {
		return Vue.http.post(
			`${this.apiUrl}?r=user/logout`,
			{ id: userId }
		)
	},

	createUser (vue, user, key) {
		const urlBase = window.location.origin // e.g. 'http://localhost:8009'
		return Vue.http.post(
			`${this.apiUrl}?r=user/new`,
			{ user: user, key: key, url_base: urlBase }
		)
	},

	confirmUser (vue, email, key) {
		return Vue.http.post(
			`${this.apiUrl}?r=user/confirm`,
			{ email: email, key: key }
		)
	},

	maskAsUser (userId) {
		return Vue.http.post(
			`${this.apiUrl}?r=user/mask`,
			{ id: userId }
		)
	},

	checkActivationKey (vue, activate_key) {
		return Vue.http.post(
			`${this.apiUrl}?r=user/check-activation-key&activation_key=${activate_key}`
		)
	},

	async activate (vue, body) {
		return Vue.http.post(
			`${this.apiUrl}?r=user/activate`,
			body
		)
	},
	resetPassword (vue, email, password, immediate) {
		const urlBase = window.location.origin // e.g. 'http://localhost:8009'
		return Vue.http.post(
			`${this.apiUrl}?r=user/reset`,
			{ email: email, password: password, url_base: urlBase, immediate: immediate }
		)
			.then((response) => {
				this.log(vue, 'user', null, 'reset',
					`User reset password: ${email}`)
				return Promise.resolve(response)
			})
	},
	userPassword (vue, user_id, password) {
		return Vue.http.post(
			`${this.apiUrl}?r=user/password`,
			{ user_id: user_id, password: password }
		)
			.then((response) => {
				this.log(vue, 'user', null, 'reset',
					`User ${user_id} password changed`)
				return Promise.resolve(response)
			})
	},
	confirmPasswordReset (vue, email, confirm) {
		return Vue.http.post(
			`${this.apiUrl}?r=user/reset`,
			{ email: email, confirm: confirm }
		)
			.then((response) => {
				this.log(vue, 'user', null, 'reset/confirm',
					`User confirmed password reset: ${email}`)
				return Promise.resolve(response)
			})
		// NB: error is handled in UserResetPassword
	},

	refreshUser () {
		return Vue.http.get(`${this.apiUrl}?r=user/refresh`)
	},

	getAppAdmin () {
		return Vue.http.get(`${this.apiUrl}?r=user/get-app-admin`)
	},

	// ---------- data endpoints -----------

	mergeCompanies (vue, companyId, mergeCompanyId) {
		return Vue.http.post(
			`${this.apiUrl}?r=company/merge`,
			{ id: companyId, merge_id: mergeCompanyId }
		)
	},

	approveCompanyUser (vue, companyUserId) {
		return Vue.http.post(
			`${this.apiUrl}?r=company-user/approve&id=${companyUserId}`
		)
	},

	denyCompanyUser (vue, companyUserId) {
		return Vue.http.post(
			`${this.apiUrl}?r=company-user/deny&id=${companyUserId}`
		)
	},

	createCompanyAndAddToCompanyUser (vue, companyUserId, company) {
		return Vue.http.post(`${this.apiUrl}?r=company-user/create-and-add-company`,
			{
				company_user_id: companyUserId,
				company: company
			})
	},

	joinCompany(vue, companyUser) {
		return Vue.http.post(`${this.apiUrl}?r=company-user/join-company`,
			{
				id: companyUser.id,
				company_user: companyUser
			})
	},

	importEquipment (vue, onRoadDataRows, nonRoadDataRows, companyId) {
		return Vue.http.post(`${this.apiUrl}?r=equipment/import`,
			{
				on_road_data_rows: onRoadDataRows,
				non_road_data_rows: nonRoadDataRows,
				company_id: companyId,
			}
		)
	},

	attestEquipment (vue, equipmentId) {
		return Vue.http.post(
			`${this.apiUrl}?r=equipment/attest&id=${equipmentId}`
		)
	},

	updateEquipmentCompliancePlan (vue, equipmentId, value) {
		return Vue.http.post(
			`${this.apiUrl}?r=equipment/update-compliance-plan&id=${equipmentId}&value=${value}`
		)
	},

	updateExemptionStatus (vue, exemptionId, status, year) {
		return Vue.http.post(
			`${this.apiUrl}?r=exemption/update-status`,
			{ id: exemptionId, status: status, year: year }
		)
	},

	updateRegistrationResolved (vue, registrationIds, value) {
		return Vue.http.post(
			`${this.apiUrl}?r=registration/update-resolved`,
			{ ids: registrationIds, value: value }
		)
	},

	numberRegistrations (vue, registrationIds, force) {
		return Vue.http.post(
			`${this.apiUrl}?r=registration/number`,
			{ ids: registrationIds, force: force }
		)
	},

	sendRegistrationMailedNotification (vue, registrationIds, force) {
		return Vue.http.post(
			`${this.apiUrl}?r=registration/notify-mailed`,
			{ ids: registrationIds }
		)
	},

	addRegistrationNote (vue, registrationIds, note) {
		return Vue.http.post(
			`${this.apiUrl}?r=registration/add-note`,
			{ ids: registrationIds, note: note }
		)
	},

	addEquipmentNote (vue, equipmentId, note) {
		return Vue.http.post(
			`${this.apiUrl}?r=equipment/add-note`,
			{ equipment_id: equipmentId, note: note }
		)
	},

	sendCustomNotification (vue, notification) {
		return Vue.http.post(
			`${this.apiUrl}?r=notification/send-custom-notification`,
			notification
		)
	},

	sendBulkNotification (vue, notification) {
		return Vue.http.post(
			`${this.apiUrl}?r=notification/send-bulk-notification`,
			notification
		)
	},

	// ---------- invitations -----------

	projectInvitationExists (email) {
		return Vue.http.post(
			`${this.apiUrl}?r=project-invitation/invitation-exists`,
			{ email: email }
		)
	},

	inviteProgramAdmin (vue, form) {
		const urlBase = window.location.origin // e.g. 'http://localhost:8009'
		return Vue.http.post(
			`${this.apiUrl}?r=invitation/invite-program-admin`,
			{ email: form.email, agency_id: form.agency_id, url_base: urlBase }
		)
	},

	inviteProjectManager (vue, form) {
		const urlBase = window.location.origin // e.g. 'http://localhost:8009'
		return Vue.http.post(
			`${this.apiUrl}?r=invitation/invite-project-manager`,
			{ email: form.email, agency_id: form.agency_id, url_base: urlBase }
		)
	},

	inviteOwnersRepresentative (vue, form) {
		const urlBase = window.location.origin // e.g. 'http://localhost:8009'
		return Vue.http.post(
			`${this.apiUrl}?r=invitation/invite-owners-representative`,
			{ email: form.email, agency_id: form.agency_id, url_base: urlBase }
		)
	},

	inviteAuditor (vue, form) {
		const urlBase = window.location.origin // e.g. 'http://localhost:8009'
		return Vue.http.post(
			`${this.apiUrl}?r=invitation/invite-auditor`,
			{ email: form.email, agency_id: form.agency_id, url_base: urlBase }
		)
	},

	inviteEquipmentOwner (vue, form) {
		const urlBase = window.location.origin // e.g. 'http://localhost:8009'
		return Vue.http.post(
			`${this.apiUrl}?r=invitation/invite-equipment-owner`,
			{ email: form.email, url_base: urlBase }
		)
	},

	sendMessageEmail (vue, message) {
		const urlBase = window.location.origin // e.g. 'http://localhost:8009'
		return Vue.http.post(
			`${this.apiUrl}?r=message/send-email`,
			{ message: message, url_base: urlBase }
		)
	},

	sendGroupEmail (vue, message) {
		const urlBase = window.location.origin // e.g. 'http://localhost:8009'
		return Vue.http.post(
			`${this.apiUrl}?r=message/send-group-email`,
			{message: message, url_base: urlBase}
		)
	},

	resendInvitation (vue, invite) {
		return Vue.http.post(
			`${this.apiUrl}?r=invitation/resend`,
			{ invite: invite }
		)
	},

	// ---------- reports -----------

	complianceChartData (vue) {
		return Vue.http.post(
			`${this.apiUrl}?r=reports/compliance-chart-data`
		)
	},

	userChartData (vue) {
		return Vue.http.post(
			`${this.apiUrl}?r=reports/user-chart-data`
		)
	},

	equipmentBreakdownChartData (vue) {
		return Vue.http.post(
			`${this.apiUrl}?r=reports/equipment-breakdown-chart-data`
		)
	},

	exemptionBreakdownChartData (vue) {
		return Vue.http.post(
			`${this.apiUrl}?r=reports/exemption-breakdown-chart-data`
		)
	},

	companyBreakdownChartData (vue, start, end) {
		return Vue.http.post(
			`${this.apiUrl}?r=reports/company-breakdown-chart-data`,
			{ start: start, end: end }
		)
	},
	// ---------- audits -----------

	completeAudit (vue, auditId) {
		return Vue.http.post(
			`${this.apiUrl}?r=audit/complete`,
			{ id: auditId }
		)
	},

	reopenAudit (vue, auditId) {
		return Vue.http.post(
			`${this.apiUrl}?r=audit/reopen`,
			{ id: auditId }
		)
	},

	// ---------- project -----------

	addSubcontractors (vue, project) {
		return Vue.http.post(
			`${this.apiUrl}?r=project/add-subcontractors`,
			{ project: project }
		)
	},

	attestProject (vue, projectId, companyId) {
		return Vue.http.post(
			`${this.apiUrl}?r=project/attest&project_id=${projectId}&company_id=${companyId}`
		)
	},

	closeProject (vue, projectId) {
		return Vue.http.post(
			`${this.apiUrl}?r=project/close&id=${projectId}`
		)
	},

	reopenProject (vue, projectId) {
		return Vue.http.post(
			`${this.apiUrl}?r=project/reopen&id=${projectId}`
		)
	},

	publicMapData (vue) {
		return Vue.http.post(
			`${this.apiUrl}?r=project/public-map-data`
		)
	},

	// ---------- logging -----------

	// action is 'create', 'update', 'delete'
	log (vue, table, item, action, message = null) {
		const Table = _.capitalize(table)
		const id = (item && item.id ? `#${item.id}` : '')

		let text = ''
		if (item && ['create', 'update', 'delete'].indexOf(action) !== -1) {
			let name = item.name
			// if (table === 'participant') {
			//     name = item.firstname + ' ' + item.lastname
			// } else if (!item.name) {
			//     name = ''
			// }
			text = `${Table} ${id} ${action}d` + (name ? `: ${name}` : '')  // e.g. 'Participant #123 updated: Jane Doe'
		} else {
			text = message
		}

		const entry = {
			entry_type: `${table}/${action}`,
			user_id: vue && vue.$user ? vue.$user.userId() : null,
			text: text,
			created: this.formatDate()
		}

		// NB: createItem has a check to avoid infinite loops!
		return this.createItem(this, 'user-log', entry)
	},

	getUserLogModelTypes () {
		return Vue.http.get(`${this.apiUrl}?r=user-log/list-model-types`)
			.then((response) => response.data)
	},

	getUserLogActionTypes () {
		return Vue.http.get(`${this.apiUrl}?r=user-log/list-action-types`)
			.then((response) => response.data)
	},

	// ----------- authorization -----------

	can (args) {
		let query = 'r=authorization/can'
		for (let key of Object.keys(args)) {
			const value = encodeURIComponent(args[key])
			query += `&${key}=${value}`
		}
		return Vue.http.get(
			`${this.apiUrl}?${query}`
		).then((response) => {
			return response
		})
	},

	// ----------- attachments -----------

	downloadAttachment (att, preview) {
		return Vue.http.get(
			`${this.apiUrl}?r=attachment/download&id=${att.id}`,
			{ responseType: 'arraybuffer' }
		)
			.then(response => {
				const url = window.URL.createObjectURL(new Blob([response.data], { type: att.type }))
				const link = document.createElement('a')
				link.href = url
				if (preview) {
					link.setAttribute('target', '_blank')
				} else {
					link.setAttribute('download', att.name)
				}
				document.body.appendChild(link)
				link.click()
			})
	},

	// TODO: how to log uploads??
	getAttachmentUploadUrl (modelClass, modelId, group) {
		return `${this.apiUrl}?r=attachment/upload&model_class=${modelClass}&model_id=${modelId}&group=${group}`
	},

	// ----------- generic restful methods -----------

	async getItems (vue, table, filters, listParams, expands) {
		return this.getResources(vue, table, false, expands, filters, listParams)
	},

	async getAllItems (vue, table, expands = []) {
		const filters = null
		const listParams = { perPage: 100000, currentPage: 1 }
		return this.getResources(vue, table, false, expands, filters, listParams)
	},

	async getItem (vue, table, id, expands = []) {
		if (!id) {
			throw '[API.getItem] Request has no ID'
		}
		const filters = {
			id: { field: `${table.replace('-', '_')}.id`, value: id }
		}
		return this.getResources(vue, table, true, expands, filters)
	},

	async createItem (vue, table, item) {
		const response = await Vue.http.post(
			`${this.apiUrl}?r=${table}/create`,
			item
		)
		// if (table !== 'user-log') { // avoid infinite loop when creating userlog entry!
		//     this.log(vue, table, item, 'create')
		// }
		return response
	},

	async updateItem (vue, table, item, log = true) {
		const response = await Vue.http.post(
			`${this.apiUrl}?r=${table}/update&id=${item.id}`,
			item
		)
		// if (log) {
		//     this.log(vue, table, item, 'update')
		// }
		return response
	},

	async deleteItem (vue, table, item) {
		const response = await Vue.http.delete(
			`${this.apiUrl}?r=${table}/delete&id=${item.id}`
		)
		this.log(vue, table, item, 'delete')
		return response
	},

	// ------- misc ---------

	// create an SQL datetime value for the current time (e.g. '2013-02-04 22:44:30')
	formatDate () {
		const d = moment().toISOString() // 2013-02-04T22:44:30.652Z
		return d.split('.')[0].replace('T', ' ')
	},

	// send a support request email
	async sendSupportRequest (info, body) {
		const response = await Vue.http.post(
			`${this.apiUrl}?r=support/send-support-email`,
			{ info: info, body: body }
		)
		return response
	},

	/**
	 * A multi-purpose method for retrieving resources from the Yii 2 api - this can be used to fetch
	 * a single resource or a list; the filters and listParams can be bound to a table for
	 * paging/sorting/filtering support.
	 *
	 * @param table The Yii model table name (in snake case - e.g. 'plan_resource')
	 * @param single Whether to fetch a single resource or a list
	 * @param expands Optional related records to fetch as part of the request
	 * @param filters Filters on model fields (use an 'id' filter to fetch a single resource)
	 * @param listParams Paging/sorting options, bound to a table
	 */
	async getResources (vue,
		table,
		single,
		expands = [],
		filters = undefined,
		listParams = undefined) {
		let params = { r: table }
		if (expands && expands.length) {
			params.expand = expands.join(',')
		}
		if (listParams) {
			params.perPage = listParams.perPage
			params.page = listParams.currentPage - 1
			params.sort = (listParams.sortDesc ? '-' : '') + listParams.sortBy
		}
		if (filters) {
			params.filters = _.cloneDeep(filters)
			// prune the <select> options from each filter, if present
			// - as these can get really long and lead to a Request-URI Too Long error
			// (they aren't needed on the server anyway)
			_.each(params.filters, (f) => {
				f.options = undefined
				f.label = undefined
			})
		}
		if (single) {
			params.perPage = 1
		}

		try {
			const response = await Vue.http.get(this.apiUrl, {
				params: params
			})
			if (listParams) {
				listParams.totalRows = response.body._meta.totalCount
			}
			if (single) {
				return (response.body.items[0])
			} else {
				return (response.body.items)
			}
		} catch (e) {
			if (vue && !process.env.VUE_APP_API_HOST) { // only show in development
				Utils.handleError(e, `Error loading data (table:${table}, filters:${JSON.stringify(filters)})`)
			}
		}
	}
}
